import React, { useState, useEffect } from "react";
import { db } from "../../../firebase";
import {
  collection,
  getDocs,
  doc,
  deleteDoc,
  updateDoc,
  addDoc,
  query,
  orderBy,
} from "firebase/firestore";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { 
  Modal, 
  Button, 
  Form, 
  Table, 
  Card, 
  Container, 
  Row, 
  Col, 
  InputGroup, 
  FormControl, 
  Badge, 
  Spinner,
  Pagination
} from "react-bootstrap";
import AdminHeader from "../../../Components/AdminHeader";
import Sidebar from "../../../Components/Sidebar";
import { FaSearch, FaEdit, FaTrash, FaUserPlus, FaUsers, FaExclamationTriangle } from "react-icons/fa";

const UsersData = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [vehicleUse, setVehicleUse] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;
  const pageLimit = 5; // Maximum number of pagination items to display at once

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    // Filter users based on search term
    if (searchTerm) {
      const filtered = users.filter(user => 
        (user.name && user.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.phoneNumber && user.phoneNumber.includes(searchTerm)) ||
        (user.address && user.address.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredUsers(filtered);
      setCurrentPage(1); // Reset to first page when filtering
    } else {
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const usersCollection = collection(db, "Users");
      // Sort by name if possible
      const usersQuery = query(usersCollection, orderBy("name", "asc"));
      const snapshot = await getDocs(usersQuery);
      
      const usersData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      setUsers(usersData);
      setFilteredUsers(usersData);
    } catch (error) {
      console.error("Error fetching users: ", error);
      toast.error("Failed to fetch users. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddNewUser = () => {
    setCurrentUserId(null);
    setName("");
    setEmail("");
    setAddress("");
    setPhoneNumber("");
    setVehicleUse("");
    setShowModal(true);
  };

  const handleEdit = (userId) => {
    const userToEdit = users.find((user) => user.id === userId);
    if (userToEdit) {
      setCurrentUserId(userId);
      setName(userToEdit.name || "");
      setEmail(userToEdit.email || "");
      setAddress(userToEdit.address || "");
      setPhoneNumber(userToEdit.phoneNumber || "");
      setVehicleUse(userToEdit.vehicleUse || "");
      setShowModal(true);
    }
  };

  const handleShowDeleteConfirm = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    
    setLoading(true);
    try {
      const userRef = doc(db, "Users", userToDelete.id);
      await deleteDoc(userRef);
      
      // Update local state
      setUsers(users.filter(user => user.id !== userToDelete.id));
      setFilteredUsers(filteredUsers.filter(user => user.id !== userToDelete.id));
      
      setShowDeleteModal(false);
      toast.success("User deleted successfully!");
    } catch (error) {
      console.error("Error deleting user: ", error);
      toast.error("Failed to delete user. Please try again later.");
    } finally {
      setLoading(false);
      setUserToDelete(null);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!name.trim() || !email.trim() || !phoneNumber.trim()) {
      toast.error("Name, email and phone number are required.");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    
    setLoading(true);
    try {
      const userData = {
        name: name.trim(),
        email: email.trim(),
        address: address.trim(),
        phoneNumber: phoneNumber.trim(),
        vehicleUse: vehicleUse.trim(),
      };
      
      if (currentUserId) {
        // Update existing user
        const userRef = doc(db, "Users", currentUserId);
        await updateDoc(userRef, userData);
        
        // Update local state
        setUsers(users.map(user => 
          user.id === currentUserId ? { id: currentUserId, ...userData } : user
        ));
        
        toast.success("User updated successfully!");
      } else {
        // Add new user
        const docRef = await addDoc(collection(db, "Users"), userData);
        
        // Update local state
        setUsers([...users, { id: docRef.id, ...userData }]);
        
        toast.success("User added successfully!");
      }
      
      // Clear form fields and close modal
      handleCloseModal();
    } catch (error) {
      console.error("Error updating user: ", error);
      toast.error("Failed to update user. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentUserId(null);
    setName("");
    setEmail("");
    setAddress("");
    setPhoneNumber("");
    setVehicleUse("");
  };

  // Get current users for pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  // Function to render pagination items
  const renderPaginationItems = () => {
    let items = [];
    let startPage = Math.max(1, currentPage - Math.floor(pageLimit / 2));
    let endPage = startPage + pageLimit - 1;
    
    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - pageLimit + 1);
    }

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => setCurrentPage(number)}
        >
          {number}
        </Pagination.Item>
      );
    }
    return items;
  };

  return (
    <div className="admin-dashboard d-flex flex-column min-vh-100">
      <ToastContainer position="top-right" autoClose={3000} />
      <AdminHeader />
      
      <div className="d-flex flex-grow-1">
        <Sidebar />
        
        <main className="flex-grow-1 bg-light">
          <Container fluid className="py-4">
            <Card className="shadow-sm border-0">
              <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <h5 className="mb-0 font-weight-bold text-primary">
                    <FaUsers className="me-2" /> Users Management
                  </h5>
                  {loading ? (
                    <Spinner animation="border" size="sm" className="ms-3" />
                  ) : (
                    <Badge bg="info" className="ms-2">
                      {filteredUsers.length}
                    </Badge>
                  )}
                </div>
                
                <div className="d-flex">
                  <InputGroup className="me-3" style={{ width: "250px" }}>
                    <InputGroup.Text>
                      <FaSearch />
                    </InputGroup.Text>
                    <FormControl
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                  
                  <Button variant="primary" onClick={handleAddNewUser}>
                    <FaUserPlus className="me-2" /> Add New User
                  </Button>
                </div>
              </Card.Header>
              
              <Card.Body className="p-0">
                {loading && users.length === 0 ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted">Loading users...</p>
                  </div>
                ) : filteredUsers.length > 0 ? (
                  <div className="table-responsive">
                    <Table className="table-hover mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th className="py-3">Name</th>
                          <th className="py-3">Email</th>
                          <th className="py-3">Phone Number</th>
                          <th className="py-3">Address</th>
                          <th className="py-3">Vehicle Use</th>
                          <th className="py-3 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentUsers.map((user) => (
                          <tr key={user.id}>
                            <td className="py-3 fw-bold">{user.name || "N/A"}</td>
                            <td className="py-3">
                              {user.email ? (
                                <a href={`mailto:${user.email}`} className="text-decoration-none">
                                  {user.email}
                                </a>
                              ) : "N/A"}
                            </td>
                            <td className="py-3">{user.phoneNumber || "N/A"}</td>
                            <td className="py-3">{user.address || "N/A"}</td>
                            <td className="py-3">{user.vehicleUse || "N/A"}</td>
                            <td className="py-3">
                              <div className="d-flex justify-content-center">
                                <Button 
                                  variant="outline-primary" 
                                  size="sm" 
                                  className="me-2"
                                  onClick={() => handleEdit(user.id)}
                                  disabled={loading}
                                  title="Edit User"
                                >
                                  <FaEdit />
                                </Button>
                                <Button 
                                  variant="outline-danger" 
                                  size="sm"
                                  onClick={() => handleShowDeleteConfirm(user)}
                                  disabled={loading}
                                  title="Delete User"
                                >
                                  <FaTrash />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <FaExclamationTriangle size={48} className="text-muted mb-3" />
                    <h5>No users found</h5>
                    <p className="text-muted">
                      {searchTerm ? "No users match your search criteria." : "There are no users in the system."}
                    </p>
                    {searchTerm ? (
                      <Button variant="outline-primary" onClick={() => setSearchTerm("")}>
                        Clear Search
                      </Button>
                    ) : (
                      <Button variant="primary" onClick={handleAddNewUser}>
                        <FaUserPlus className="me-2" /> Add First User
                      </Button>
                    )}
                  </div>
                )}
              </Card.Body>
              
              {filteredUsers.length > 0 && (
                <Card.Footer className="bg-white border-0 px-4 py-3">
                  <Row className="align-items-center">
                    <Col>
                      <small className="text-muted">
                        Showing {filteredUsers.length > 0 ? indexOfFirstUser + 1 : 0} to {Math.min(indexOfLastUser, filteredUsers.length)} of {filteredUsers.length} users
                      </small>
                    </Col>
                    <Col className="d-flex justify-content-end">
                      {totalPages > 1 && (
                        <Pagination className="mb-0">
                          <Pagination.First
                            onClick={() => setCurrentPage(1)}
                            disabled={currentPage === 1}
                          />
                          <Pagination.Prev
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                          />
                          {renderPaginationItems()}
                          <Pagination.Next
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          />
                          <Pagination.Last
                            onClick={() => setCurrentPage(totalPages)}
                            disabled={currentPage === totalPages}
                          />
                        </Pagination>
                      )}
                    </Col>
                  </Row>
                </Card.Footer>
              )}
            </Card>
          </Container>
        </main>
      </div>

      {/* Add/Edit User Modal */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            {currentUserId ? (
              <><FaEdit className="me-2" /> Edit User</>
            ) : (
              <><FaUserPlus className="me-2" /> Add New User</>
            )}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdate}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="editName">
                  <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    placeholder="Enter full name"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="editEmail">
                  <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="Enter email address"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="editPhoneNumber">
                  <Form.Label>Phone Number <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    required
                    placeholder="Enter phone number"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="editVehicleUse">
                  <Form.Label>Vehicle Use</Form.Label>
                  <Form.Control
                    type="text"
                    value={vehicleUse}
                    onChange={(e) => setVehicleUse(e.target.value)}
                    placeholder="Enter vehicle use"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3" controlId="editAddress">
              <Form.Label>Address</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Enter full address"
              />
            </Form.Group>
            
            <small className="text-muted">Fields marked with <span className="text-danger">*</span> are required</small>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? (
                <><Spinner as="span" animation="border" size="sm" className="me-2" /> Saving...</>
              ) : currentUserId ? (
                "Update User"
              ) : (
                "Add User"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal 
        show={showDeleteModal} 
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div className="mb-3 text-danger">
            <FaTrash size={48} />
          </div>
          <h5>Are you sure you want to delete this user?</h5>
          {userToDelete && (
            <p className="text-muted mb-0">
              User: <strong>{userToDelete.name || "Unknown"}</strong>
              <br />
              Email: {userToDelete.email || "N/A"}
            </p>
          )}
          <p className="text-danger mt-3">
            This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={loading}
          >
            {loading ? (
              <><Spinner as="span" animation="border" size="sm" className="me-2" /> Deleting...</>
            ) : (
              "Delete User"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UsersData;